<template>
  <div id="blog-homepage" class="w-100 flex flex-column pt60">
    <div v-if="loading" class="fixed top-0 left-0 w-100 h-100 flex middle-xs center-xs z-1000" style="background: rgba(0, 0, 0, 0.5);">
      <div class="flex middle-xs cl-white" style="font-size: 24px; text-align: center;">
        <div class="spinner mr10"></div>
        <span>{{ $t('Loading...') }}</span>
      </div>
    </div>
    <div v-else class="w-100">
      <!-- Breadcrumbs -->
      <div class="container py15">
        <breadcrumbs :routes="breadcrumbs" :active-route="$t('Blog')" />
      </div>

      <!-- Render tile rows from blog homepage data -->
      <div
        v-for="(component, index) in componentList"
        :key="`blog-component-${index}`"
        class="mb30"
      >
        <lazy-hydrate when-visible>
          <div
            v-show="isTileRow(component)"
            class="container"
          >
            <advertising-tiles :partner-tiles="component.tileList" />
          </div>
        </lazy-hydrate>
      </div>
    </div>
  </div>
</template>

<script>
import { Logger } from '@vue-storefront/core/lib/logger'
import { mapState } from 'vuex'
import BLOG_HOMEPAGE_QUERY from '../../graphql/strapi/queries/blogHomepages.gql'
import AdvertisingTiles from 'theme/components/theme/blocks/Home/AdvertisingTiles'
import { getApolloProvider } from '@vue-storefront/core/scripts/resolvers/resolveGraphQL'
import Breadcrumbs from 'theme/components/core/Breadcrumbs'
import { getCmsPartnerId } from '../../helpers'

const homepageComponentTypeMap = {
  ComponentHomepageTileRow: 'ComponentHomepageTileRow'
}

const transformBlogHomepageQueryResponse = (blogHomepageQueryResponse) => {
  const componentList = blogHomepageQueryResponse?.data?.blogHomepages?.data[0]?.attributes?.BlogRows
    .map((component) => {
      switch (component.__typename) {
        case homepageComponentTypeMap.ComponentHomepageTileRow:
          if (!component.Tile?.length) {
            return null;
          }

          return {
            type: component.__typename,
            tileList: component.Tile.map(({ id, Text, Link, ButtonText, BackgroundImage, ImageAltText, BackgroundColor }) => ({
              id,
              text: Text,
              link: `/blog${Link}`,
              buttonText: ButtonText,
              backgroundImageUrl: BackgroundImage?.data?.attributes.url,
              imageAltText: ImageAltText,
              backgroundColor: BackgroundColor
            }))
          }

        default:
          return null;
      }
    })
    .filter((component) => !!component);

  return componentList;
}

export default {
  name: 'BlogHomepage',
  components: {
    AdvertisingTiles,
    Breadcrumbs
  },
  data () {
    return {
      loading: true,
      componentList: [],
      breadcrumbs: [
        {
          name: this.$t('Home'),
          route_link: '/'
        }
      ]
    }
  },
  methods: {
    isTileRow (component) {
      return component.type === homepageComponentTypeMap.ComponentHomepageTileRow;
    },
    async fetchBlogHomepageData (partnerId) {
      try {
        const blogHomepageQueryResponse = await this.$apollo.query({
          query: BLOG_HOMEPAGE_QUERY,
          variables: {
            'filters': {
              'partner': {
                'Name': {
                  'eq': partnerId
                }
              }
            }
          }
        });

        this.componentList = transformBlogHomepageQueryResponse(blogHomepageQueryResponse);
        this.loading = false;
      } catch (error) {
        Logger.error('Cannot load data on blog homepage: ' + error);
        this.loading = false;
      }
    }
  },
  computed: {
    ...mapState({
      storeView: state => state.storeView
    }),
    cmsPartnerId () {
      return getCmsPartnerId(this.storeView);
    }
  },
  mounted () {
    this.fetchBlogHomepageData(this.cmsPartnerId);
  },
  serverPrefetch () {
    return this.fetchBlogHomepageData(this.cmsPartnerId);
  },
  async asyncData ({ store, context, route, app }) { // this is for SSR purposes to prefetch data
    if (context) context.output.cacheTags.add(`blog`)
    Logger.info('Calling asyncData in BlogHomepage (theme)')()
    const apolloProvider = await getApolloProvider();
    const apolloClient = apolloProvider.defaultClient;
    try {
      const response = await apolloClient.query({
        query: BLOG_HOMEPAGE_QUERY,
        variables: {
          filters: {
            partner: {
              Name: {
                eq: getCmsPartnerId(store.state.storeView)
              }
            }
          }
        }
      });

      const componentList = transformBlogHomepageQueryResponse(response)

      return {
        componentList
      };
    } catch (error) {
      console.error('Error in asyncData for BlogHomepage: ', error);
      return { componentList: [] };
    }
  },
  metaInfo () {
    return {
      title: this.$t('Blog'),
      meta: [{ vmid: 'description', name: 'description', content: this.$t('Blog Homepage') }]
    }
  }
}
</script>

<style lang="scss" scoped>
.blog-component-container {
  margin-bottom: 30px;
}

.full-screen-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loader-content {
  display: flex;
  place-items: center;
  color: white;
  font-size: 24px;
  text-align: center;
}

.spinner {
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid var(--c-primary);
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin-right: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
